var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { AsciiText } from './framework/AsciiText';
import { defaultSocketOptions, } from './framework/CommandSocket';
import { Timeouts } from './framework/Timeouts';
export class SamsungPro {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.log = log;
        this.framework = framework;
        // other class properties and constructor
        this.codeOffset = 0x30;
        this.isMacAddressSpecified = false;
        this.timeouts = new Timeouts();
        this.busyTimeout = this.timeouts.create();
        this.deviceId = 0x00;
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(this.makeCommand(0x0e), /\xaa\xff[\s\S][\s\S]\x41\x0e([\s\S]+)[\s\S]/, tags);
                return match[1];
            }),
            powerOffRestrictions: ['get'],
        };
        this.power = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                if (!this.isMacAddressSpecified)
                    yield this.queryStatus(false);
                return this.state.power.value;
            }),
            set: (power, tags) => __awaiter(this, void 0, void 0, function* () {
                // Clear any pending busy timeouts that would set busy back to false. The power set
                // methods will set busy to true and manage the transition back to false.
                this.busyTimeout.clear();
                if (this.isMacAddressSpecified) {
                    if (this.socket.options.allowNoConnection) {
                        return this.setPowerWakeOnLanNetwork(power, tags);
                    }
                    else {
                        return this.setPowerWakeOnLanSerial(power, tags);
                    }
                }
                return this.setPowerSimple(power, tags);
            }),
            powerOffRestrictions: (config) => {
                return config.macAddress === undefined ? [] : ['get'];
            },
        };
        this.serialNumber = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                var _a;
                return (_a = this.savedSerialNumber) !== null && _a !== void 0 ? _a : 'unknown';
            }),
            hasControl: (config) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(this.makeCommand(0x0b), /\xaa\xff[\s\S]\x14\x41\x0b([^\x00]+)[\s\S]{4}|\xaa\xff[\s\S]\x03\x4e\x0b[\s\S]{2}/);
                this.savedSerialNumber = match[1];
                return this.savedSerialNumber !== undefined;
            }),
            powerOffRestrictions: ['get', 'hasControl'],
        };
        this.videoInput = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.videoInput.value;
            }),
            set: (code, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(this.makeCommand(0x14, code.charCodeAt(0) - this.codeOffset), /\xaa\xff[\s\S]\x03([\s\S])\x14([\s\S])[\s\S]/, tags, { sendTimeoutMs: 6000 } // this command sometimes takes a while to respond
                );
                this.state.busy.update(true);
                this.busyTimeout.set(() => this.state.busy.update(false), 2000);
                if (match[1] === 'A') {
                    return String.fromCharCode(match[2].charCodeAt(0) + this.codeOffset);
                }
                return this.state.videoInput.value;
            }),
        };
        this.volume = {
            range: { max: 100, min: 0, round: 1 },
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.volume.value;
            }),
            set: (volume, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(this.makeCommand(0x12, volume), /\xaa\xff[\s\S]\x03([\s\S])\x12([\s\S])[\s\S]/, tags);
                if (match[1] === 'A')
                    return match[2].charCodeAt(0);
                return this.state.volume.value;
            }),
        };
        this.volumeMute = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.volumeMute.value;
            }),
            set: (mute, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(this.makeCommand(0x13, mute ? 1 : 0), /\xaa\xff[\s\S]\x03([\s\S])\x13([\s\S])[\s\S]/, tags);
                return match[1] === 'A' ? match[2].charCodeAt(0) === 1 : this.state.volumeMute.value;
            }),
        };
        // polling
        this.polls = [
            { intervalMs: 3000, function: this.queryStatus.bind(this) },
            {
                intervalMs: 5000,
                pollWhenDisconnected: true,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    if (!this.state.power.value &&
                        this.socket.options.allowNoConnection &&
                        this.socket.getState() !== 'connected') {
                        if (!this.framework.isReconnecting)
                            yield this.framework.reconnect();
                    }
                }),
            },
        ];
        if (this.socket.options.allowNoConnection) {
            // If the display is powered off externally (e.g., IR remote or third party controller),
            // then we will lose the socket connection. If this happens, we update the power state
            // to false.
            this.socket.onConnectionLost(() => {
                this.state.power.update(false, true);
            });
        }
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
            this.isMacAddressSpecified = config.macAddress !== undefined;
            if (this.isMacAddressSpecified && config.port === undefined) {
                this.log.error(`mac address configured for wake on lan, but no port specified`);
            }
            if (this.isMacAddressSpecified) {
                // The mac address is specified, so we can't query the power if the device is powered
                // off. The power is initialized to false, but we need to query it if we can when we
                // first connect.
                if (this.socket.port !== 1515 || this.socket.state === 'connected') {
                    yield this.queryStatus(false);
                    this.state.busy.update(false);
                    this.state.power.update(yield this.power.get(['power.get']));
                }
            }
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    // other class methods
    makeCommand(command, data = []) {
        if (typeof data === 'number')
            data = [data];
        // [0xAA] [command] [ID] [DataLength] [data: 0-36 bytes] [checksum]
        data.unshift(data.length);
        data.unshift(this.deviceId); // add id
        data.unshift(command); // add command
        data.push(data.reduce((acc, curr) => acc + curr, 0) & 0x00ff); // add checksum
        data.unshift(0xaa); // add header
        return AsciiText.decode(data);
    }
    queryStatus(isPoll = true) {
        return __awaiter(this, void 0, void 0, function* () {
            // We use the status control command to poll the display's values. First, it is more
            // efficient to send a single command to query all the values. Second, some of the values do
            // not respond to individual queries when the display is powered off.
            const match = yield this.socket.send(this.makeCommand(0x00), /\xAA\xFF[\s\S]\x09([\s\S])\x00([\s\S]{8})|\xAA\xFF[\s\S]\x03[\s\S]{4}/, isPoll ? ['poll', 'get.status'] : ['get.status']);
            if (match[1] === 'A') {
                const data = match[2];
                this.state.power.update(data.charCodeAt(0) === 1);
                this.state.volume.update(data.charCodeAt(1));
                this.state.volumeMute.update(data.charCodeAt(2) === 1);
                // On the display we tested, this command returns 0x1f instead of 0x18 for the DVI
                // input. We translate it to the correct value here.
                let videoCode = data.charCodeAt(3);
                if (videoCode === 0x1f)
                    videoCode = 0x18;
                this.state.videoInput.update(String.fromCharCode(videoCode + this.codeOffset));
            }
        });
    }
    setPowerSimple(power, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            this.state.busy.update(true);
            const match = yield this.socket.send(this.makeCommand(0x11, power ? 0x01 : 0x00), /\xaa\xff[\s\S]\x03([\s\S])\x11([\s\S])[\s\S]/, tags);
            const newPower = match[1] === 'A' ? match[2].charCodeAt(0) === 1 : this.state.power.value;
            if (power && this.socket.port === 1515) {
                // When this device powers on, it closes the socket. So, if we are connected via the
                // direct network connection (as opposed to an Ethernet to serial converter), we tell
                // the framework to reconnect once the display has had time to power on and respond to
                // incoming sockets. Note that this automatically suspends the framework and socket
                // until the reconnect is complete.
                this.state.power.update(newPower);
                yield this.framework.reconnect({ initialDelayMs: 13000 });
            }
            else {
                this.state.power.update(newPower);
                yield this.timeouts.delay(power ? 14000 : 4000);
            }
            this.state.busy.update(false);
            return newPower;
        });
    }
    setPowerWakeOnLanNetwork(power, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            this.framework.suspendPolling(6000);
            this.socket.removeCommands('poll');
            if (power) {
                // If the socket is still connected, send a power command in case wake on lan is not
                // required.
                if (this.socket.state === 'connected') {
                    yield this.socket.send(this.makeCommand(0x11, power ? 0x01 : 0x00), /\xaa\xff[\s\S]\x03([\s\S])\x11([\s\S])[\s\S]/, tags);
                }
                // For direct Ethernet connections where the socket connection is lost when the display
                // is powered off, we first update the power state so the UI will reflect that the power
                // is on, then we block while sending a wake on lan request and attempting to reconnect
                // to the display.
                this.state.busy.update(true);
                // Update power with a short delay so that framework.reconnect gets called first. This
                // tells StateBridge we are disconnected so it doesn't change phase back to configuring
                // to query the controls with power off restrictions when it sees that the power is
                // back on.
                this.timeouts.set(() => this.state.power.update(power), 100);
                // Testing to see if longer attempt delay (30000) allows SamsungPro to boot up with
                // valid network settings.  Reduced number of attempts to 4 as part of test.
                yield this.framework.reconnect({
                    attemptDelayMs: 30000,
                    initialDelayMs: 0,
                    numAttempts: 4,
                    onBeforeReconnect: () => __awaiter(this, void 0, void 0, function* () {
                        return this.framework.sendWakeOnLan();
                    }),
                });
                // When updating the busy state, we must use notifyIfUnchanged = true. This is because
                // the driver reconnects as part of powering on, and reconnecting resets all the states.
                // Without notifyIfUnchanged = true, the busy = false notification won't get sent.
                this.state.busy.update(false, true);
                return this.socket.state === 'connected';
            }
            else {
                const match = yield this.socket.send(this.makeCommand(0x11, power ? 0x01 : 0x00), /\xaa\xff[\s\S]\x03([\s\S])\x11([\s\S])[\s\S]/, tags);
                this.state.busy.update(true);
                if (match[1] === 'A') {
                    const newPower = match[2].charCodeAt(0) === 1;
                    this.state.power.update(newPower);
                    yield this.timeouts.delay(2000);
                }
                this.state.busy.update(false);
                return this.state.power.value;
            }
        });
    }
    setPowerWakeOnLanSerial(power, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            this.framework.suspendPolling(6000);
            this.socket.removeCommands('poll');
            if (power) {
                // For serial connections where the display is unresponsive when powered off, first we
                // update the power state so the UI will reflect that the power is on, then we block
                // while sending a wake on lan request, then we continue blocking while sending some
                // power requests to confirm that the power is on, finally we return the new power
                // state.
                this.state.busy.update(true);
                this.state.power.update(true);
                let newPower = false;
                for (let i = 0; i < 4; i++) {
                    this.framework.suspendPolling(6000);
                    yield this.framework.sendWakeOnLan();
                    try {
                        const match = yield this.socket.send(this.makeCommand(0x11, power ? 0x01 : 0x00), /\xaa\xff[\s\S]\x03([\s\S])\x11([\s\S])[\s\S]/, tags);
                        if (match[1] === 'A') {
                            newPower = match[2].charCodeAt(0) === 1;
                            break;
                        }
                    }
                    catch (_a) { }
                }
                this.state.busy.update(false);
                return newPower;
            }
            else {
                return this.setPowerSimple(power, tags);
            }
        });
    }
}
// static properties
SamsungPro.driverType = 'socketDriver';
SamsungPro.key = '-LBR-2ML9LcxruxmjyEH';
SamsungPro.socketOptions = (config) => {
    const baseSocketOptions = {
        commandExtractor: (buffer) => {
            // Command Format:
            //   [0xAA] [command] [ID] [DataLength] [data: 0-36 bytes] [checksum]
            //
            // Try each byte in the buffer as a starting position, in case we receive
            // any corrupted data.
            for (let i = 0; i < buffer.length - 4; i++) {
                if (buffer.charCodeAt(i) !== 0xaa)
                    continue;
                if (i > buffer.length - 4)
                    break;
                if (i > buffer.length - 5 - buffer.charCodeAt(i + 3))
                    break;
                const endPos = i + 4 + buffer.charCodeAt(i + 3);
                let checksum = 0;
                for (let j = i + 1; j < endPos; j++) {
                    checksum += buffer.charCodeAt(j);
                }
                checksum &= 0x00ff;
                if (checksum === buffer.charCodeAt(endPos)) {
                    const command = buffer.slice(i, endPos + 1);
                    buffer = buffer.slice(endPos + 1);
                    return { command, buffer };
                }
            }
            return undefined;
        },
        isBinary: true,
        sendTerminator: '',
    };
    const hasMacAddress = config.macAddress !== undefined;
    if (config.port === undefined) {
        return [
            Object.assign(Object.assign({}, baseSocketOptions), { port: 1515, allowNoConnection: hasMacAddress }),
            Object.assign(Object.assign({}, baseSocketOptions), { port: 4999, idleTimeoutMs: hasMacAddress ? 0 : defaultSocketOptions.idleTimeoutMs, allowNoConnection: false }),
        ];
    }
    // Assume port 1515 is direct Ethernet to the device and anything else is via an Ethernet
    // to serial converter.
    return [
        Object.assign(Object.assign({}, baseSocketOptions), { port: config.port, idleTimeoutMs: hasMacAddress && config.port !== 1515 ? 0 : defaultSocketOptions.idleTimeoutMs, allowNoConnection: hasMacAddress && config.port === 1515 }),
    ];
};
